"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/casting/Vacuum-Casting/Control-System";

const QC_IMAGES = [
	`${IMAGE_BASE}/cmm.webp`,
	`${IMAGE_BASE}/altimeter.webp`,
	`${IMAGE_BASE}/measuring-machine-1.webp`,
	`${IMAGE_BASE}/contour-measuring-instrument-8.JPG`,
] as const;

const ISO_BADGES = [
	{ src: `${IMAGE_BASE}/iso-9001.png`, altKey: "qualityControlIso1Alt" as const },
	{ src: `${IMAGE_BASE}/iso-13485.png`, altKey: "qualityControlIso2Alt" as const },
	{ src: `${IMAGE_BASE}/iso-14001.png`, altKey: "qualityControlIso3Alt" as const },
	{ src: `${IMAGE_BASE}/iatf.png`, altKey: "qualityControlIso4Alt" as const },
] as const;

const CONTENT_BLOCKS = [
	{ titleKey: "qualityControlBlock1Title" as const, descKey: "qualityControlBlock1Desc" as const },
	{ titleKey: "qualityControlBlock2Title" as const, descKey: "qualityControlBlock2Desc" as const },
	{ titleKey: "qualityControlBlock3Title" as const, descKey: "qualityControlBlock3Desc" as const },
] as const;

const CTA_HREF = "/upload";

export default function VacuumCastingQualityControl() {
	const t = useTranslations("new-vacuum-casting");

	return (
		<section className="bg-[#f6ffde] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("qualityControlTitle")}
					</h2>
					<p className="mt-3 text-sm leading-relaxed text-black sm:mt-4 md:text-base">
						{t("qualityControlDesc")}
					</p>
				</header>

				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-12">
					<div>
						<div className="grid grid-cols-2 gap-3 sm:gap-4">
							{QC_IMAGES.map((src, index) => (
								<div key={src} className="relative aspect-[4/2.6] overflow-hidden bg-white">
									<Image
										src={src}
										alt={t(`qualityControlImageAlt${index + 1}` as const)}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 50vw, 25vw"
									/>
								</div>
							))}
						</div>

						<div className="mt-6 grid grid-cols-4 items-center gap-3 sm:gap-6">
							{ISO_BADGES.map((badge) => (
								<div key={badge.src} className="relative aspect-square">
									<Image
										src={badge.src}
										alt={t(badge.altKey)}
										fill
										className="object-contain"
										sizes="(max-width: 1024px) 25vw, 120px"
									/>
								</div>
							))}
						</div>
					</div>

					<div className="flex flex-col gap-5 sm:gap-6 md:gap-7">
						{CONTENT_BLOCKS.map((block) => (
							<div key={block.titleKey}>
								<h3 className="mb-2 text-base font-bold text-themePrimary400 sm:mb-3 sm:text-lg md:text-xl">
									{t(block.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(block.descKey)}</p>
							</div>
						))}

						<div>
							<Link
								href={CTA_HREF}
								className="inline-flex items-center justify-center rounded-md bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#83b751] md:text-base"
							>
								{t("qualityControlCta")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
